import { FunctionComponent, useCallback, useState } from 'react'
import PlaylistWrapper from '@components/Video/Brightcove/PlaylistWrapper'
import VideoContainer from '@components/Video/Brightcove/VideoContainer'
import VideoWrapper from '@components/Video/Brightcove/VideoWrapper'
import { Image } from '@cook-types/cook'
import useIsInArticleContent from '@hooks/useIsInArticleContent'
import useIsInScribbleContent from '@hooks/useIsInScribbleContent'
import usePageMetadata from '@hooks/usePageMetadata'
import { useVideo } from '@hooks/useVideo'
import useInViewChange from '@hooks/useInViewChange'
import { isVideoOnlyArticle } from '@widgets/Teaser/utils'
import { CookWidget } from '@widgets/types'
import DarkCove from './DarkCove'
import Playlist from './playlist/Playlist'
import { VideoAd } from './types/darkcove'
import VideoTitle from './VideoTitle'

export interface DarkCoveWidgetAPIProps {
  ad?: VideoAd
  autoplay?: boolean
  allowVideoCropping: boolean
  catchword?: string
  catchwordArticle?: string
  duration: number
  image?: Image
  isAdDisabled: boolean
  isMainElement?: boolean
  titleArticle?: string
  uuid?: string
  videoId: string
  syndication: string
  kind: string[]
  durationInSeconds: number
  inline: boolean
  placeholderImage: Image | null
  videoAdPlacement: string
  videoAdUrl?: string
  videoSlotCode?: string
  videoSlotCodeMobile?: string
  vtype: string | undefined
  title: string
}

const DarkCoveWidget: FunctionComponent<DarkCoveWidgetAPIProps> = (props) => {
  const pageMetadata = usePageMetadata()
  const isInScribbleContent = useIsInScribbleContent()

  const [isInViewport, setIsInViewport] = useState(null)

  const onInViewChangeHandler = useCallback((inView: any) => {
    setIsInViewport(inView)
  }, [])

  const viewportRef = useInViewChange({
    threshold: 0.2,
    onInViewChange: onInViewChangeHandler,
  })

  const {
    id: articleId,
    catchword: catchwordArticle,
    title: titleArticle,
    teaser: { targetContentType } = {},
    hasPlaylist,
  } = pageMetadata

  // TODO: add isE2ETest when it is ready
  const isE2ETest = false

  const videoTaxonomy = props.ad?.metadata?.iabcategories
  const e2eAdsExcludedElement = {
    ...props,
    ...(isE2ETest ? { isAdDisabled: true } : {}),
  }

  const {
    catchword,
    duration,
    isMainElement,
    syndication,
    title,
    videoId,
    image,
    allowVideoCropping,
    isAdDisabled,
  } = e2eAdsExcludedElement

  const isInArticle = useIsInArticleContent()
  const { isOverlay } = useVideo()
  const isSameName = catchword === catchwordArticle && title === titleArticle
  const showTitle =
    !isInScribbleContent &&
    (!isSameName || !isMainElement) &&
    (!isMainElement || targetContentType !== 'video')
  const isInVideoOnlyArticle = isVideoOnlyArticle(targetContentType)

  return (
    <VideoContainer
      {...{
        videoId,
        isInOverlay: isOverlay,
        isInVideoOnlyArticle,
        hasPlaylist,
      }}>
      <VideoWrapper ref={viewportRef} hasPlaylist={hasPlaylist}>
        {isInScribbleContent ? (
          <DarkCove
            {...{
              articleId,
              videoId,
              allowVideoCropping: true,
              videoTaxonomy: [],
              duration: 40,
              isAdDisabled: !!isAdDisabled,
              syndication: 'blick',
              title: 'Scribble Video',
              autoplay: false,
              isBlickTV: false,
              isInViewport,
              allowFloating: false,
              isInVideoOnlyArticle,
              isInArticle,
              hasPlaylist: hasPlaylist,
              isMainElement,
            }}
          />
        ) : (
          <DarkCove
            {...{
              articleId,
              videoId,
              allowVideoCropping,
              videoTaxonomy,
              duration,
              isAdDisabled,
              syndication,
              image,
              title,
              isInVideoOnlyArticle,
              isInArticle,
              hasPlaylist,
              isInViewport,
              isMainElement,
            }}
            autoplay={false}
            isBlickTV={false}
            allowFloating={false}
          />
        )}

        {showTitle && <VideoTitle {...{ title, catchword, duration }} />}
      </VideoWrapper>
      {hasPlaylist && (
        <PlaylistWrapper>
          <Playlist hideInDesktop={false} />
        </PlaylistWrapper>
      )}
    </VideoContainer>
  )
}

const widget: CookWidget = {
  kind: ['widget', 'video'],
  component: DarkCoveWidget,
}

export default widget
